import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Plus, Pencil, Trash2, Power, ArrowLeft, Shield, Eye, EyeOff, Key, UserCheck, UserCog } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Roles that require employee linkage to a Master record
const EMPLOYEE_LINKED_ROLES = ['Sales Rep', 'Service Technician', 'Supervisor'];

function UserManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [users, setUsers] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [employees, setEmployees] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAddUser, setShowAddUser] = React.useState(false);
  const [showResetPassword, setShowResetPassword] = React.useState(null);
  const [showEditUser, setShowEditUser] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  
  const [newUser, setNewUser] = React.useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role_id: '',
    employee_id: '',
    employee_name: ''
  });
  
  const [editUser, setEditUser] = React.useState({
    name: '',
    phone: '',
    role_id: '',
    employee_id: '',
    employee_name: ''
  });
  
  const [newPassword, setNewPassword] = React.useState('');

  const token = localStorage.getItem('access_token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  React.useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users`, authHeader);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired or access denied');
        onLogout();
      } else {
        toast.error('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/roles/list`, authHeader);
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Fetch sales reps, service technicians, and supervisors masters in parallel
      const [salesRepsRes, techniciansRes, supervisorsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/masters?type=sales_rep`, authHeader),
        axios.get(`${BACKEND_URL}/api/masters?type=service_technician`, authHeader).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/masters?type=service_supervisor`, authHeader).catch(() => ({ data: [] })),
      ]);

      const combinedEmployees = [
        ...salesRepsRes.data.map(e => ({ ...e, empType: 'sales_rep' })),
        ...techniciansRes.data.map(e => ({ ...e, empType: 'service_technician' })),
        ...supervisorsRes.data.map(e => ({ ...e, empType: 'service_supervisor' })),
      ];

      setEmployees(combinedEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Check if selected role requires employee linkage
  const needsEmployeeLinkage = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role && EMPLOYEE_LINKED_ROLES.includes(role.name);
  };

  // Get filtered employees based on role
  const getFilteredEmployees = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return employees;

    if (role.name === 'Sales Rep') {
      return employees.filter(e => e.empType === 'sales_rep' || e.type === 'sales_rep');
    } else if (role.name === 'Service Technician') {
      return employees.filter(e => e.empType === 'service_technician' || e.type === 'service_technician');
    } else if (role.name === 'Supervisor') {
      return employees.filter(e => e.empType === 'service_supervisor' || e.type === 'service_supervisor');
    }
    return employees;
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.name || !newUser.email || !newUser.password || !newUser.role_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Check if employee linkage is required
    if (needsEmployeeLinkage(newUser.role_id) && !newUser.employee_id) {
      toast.error('Please select an employee for this role');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/users`, newUser, authHeader);
      toast.success('User created successfully');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', phone: '', role_id: '', employee_id: '', employee_name: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!editUser.name || !editUser.role_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Check if employee linkage is required
    if (needsEmployeeLinkage(editUser.role_id) && !editUser.employee_id) {
      toast.error('Please select an employee for this role');
      return;
    }

    try {
      await axios.put(`${BACKEND_URL}/api/users/${showEditUser.id}`, editUser, authHeader);
      toast.success('User updated successfully');
      setShowEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const openEditModal = (userToEdit) => {
    setEditUser({
      name: userToEdit.name,
      phone: userToEdit.phone || '',
      role_id: userToEdit.role_id,
      employee_id: userToEdit.employee_id || '',
      employee_name: userToEdit.employee_name || ''
    });
    setShowEditUser(userToEdit);
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/users/${userId}/toggle-status`,
        {},
        authHeader
      );
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {},
        authHeader
      );
      toast.success('Password reset successfully');
      setShowResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Impersonate user - "View as" feature
  const handleViewAsUser = async (targetUser) => {
    if (targetUser.email === user?.email) {
      toast.error('Cannot impersonate yourself');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/impersonate`,
        { user_id: targetUser.id },
        authHeader
      );
      
      // Store original admin token for later restoration
      localStorage.setItem('original_admin_token', response.data.original_admin_token);
      localStorage.setItem('original_admin_user', JSON.stringify(user));
      localStorage.setItem('is_impersonating', 'true');
      
      // Switch to impersonated user token and store user info
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(`Now viewing as ${targetUser.name}. Use the banner to switch back.`);
      
      // Reload to apply new session
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Company Admin': return 'bg-purple-100 text-purple-700';
      case 'Manager': return 'bg-blue-100 text-blue-700';
      case 'Sales Rep': return 'bg-green-100 text-green-700';
      case 'Supervisor': return 'bg-cyan-100 text-cyan-700';
      case 'Service Technician': return 'bg-orange-100 text-orange-700';
      case 'Viewer': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800">User Management</h1>
                  <p className="text-xs text-gray-500">{user?.company_name || 'Manage your team'}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                <p className="text-xs text-[#F4ACB7]">{user?.role_name}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{users.length}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Power className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{users.filter(u => u.is_active).length}</p>
                <p className="text-xs text-gray-500">Active Users</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{users.filter(u => u.role_name === 'Company Admin').length}</p>
                <p className="text-xs text-gray-500">Admins</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{roles.length}</p>
                <p className="text-xs text-gray-500">Roles</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Team Members</h2>
            <button
              onClick={() => setShowAddUser(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg hover:bg-[#e89aa5] transition-colors"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4" />
              Add User
            </button>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No users yet. Add your first team member to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-600">User</th>
                    <th className="text-left p-4 font-medium text-gray-600">Role</th>
                    <th className="text-left p-4 font-medium text-gray-600">Linked Employee</th>
                    <th className="text-left p-4 font-medium text-gray-600">Status</th>
                    <th className="text-left p-4 font-medium text-gray-600">Last Login</th>
                    <th className="text-right p-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFE5D9] to-[#F4ACB7] flex items-center justify-center text-white font-medium">
                            {u.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{u.name}</p>
                            <p className="text-sm text-gray-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(u.role_name)}`}>
                          {u.role_name}
                        </span>
                      </td>
                      <td className="p-4">
                        {u.employee_name ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-blue-50 text-blue-700">
                            <UserCheck className="w-3 h-3" />
                            {u.employee_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4 text-gray-500 text-sm">
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditModal(u)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit User"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setShowResetPassword(u.id)}
                            className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          {/* View as User - only show for non-admin users */}
                          {u.role_name !== 'Company Admin' && u.role_name !== 'Super Admin' && (
                            <button
                              onClick={() => handleViewAsUser(u)}
                              className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                              title={`View app as ${u.name}`}
                              data-testid={`view-as-${u.id}`}
                            >
                              <UserCog className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleStatus(u.id)}
                            className={`p-2 rounded-lg ${u.is_active ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                            title={u.is_active ? 'Disable User' : 'Enable User'}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">Add New User</h3>
              <p className="text-sm text-gray-500 mt-1">Create a new team member account</p>
            </div>
            
            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({...prev, name: e.target.value}))}
                  placeholder="John Doe"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({...prev, email: e.target.value}))}
                  placeholder="john@company.com"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-email"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({...prev, password: e.target.value}))}
                    placeholder="Min 6 characters"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7] pr-10"
                    data-testid="new-user-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser(prev => ({...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10)}))}
                  placeholder="9876543210"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-phone"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select
                  value={newUser.role_id}
                  onChange={(e) => setNewUser(prev => ({...prev, role_id: e.target.value, employee_id: '', employee_name: ''}))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-role"
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Employee Selection - Shows when role requires it */}
              {needsEmployeeLinkage(newUser.role_id) && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <UserCheck className="w-5 h-5 text-blue-600" />
                    <label className="text-sm font-medium text-blue-800">
                      Link to Employee Record *
                    </label>
                  </div>
                  <p className="text-xs text-blue-600 mb-2">
                    This user will only see data assigned to the selected employee
                  </p>
                  <select
                    value={newUser.employee_id}
                    onChange={(e) => {
                      const emp = getFilteredEmployees(newUser.role_id).find(emp => emp.id === e.target.value);
                      setNewUser(prev => ({
                        ...prev, 
                        employee_id: e.target.value,
                        employee_name: emp?.name || ''
                      }));
                    }}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                    data-testid="new-user-employee"
                    required
                  >
                    <option value="">Select Employee</option>
                    {getFilteredEmployees(newUser.role_id).map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                  {getFilteredEmployees(newUser.role_id).length === 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      ⚠️ No employees found. Please add sales reps in Masters first.
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowAddUser(false); setNewUser({ name: '', email: '', password: '', phone: '', role_id: '', employee_id: '', employee_name: '' }); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg hover:bg-[#e89aa5]"
                  data-testid="create-user-btn"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Reset Password</h3>
              <p className="text-sm text-gray-500 mt-1">Set a new password for this user</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowResetPassword(null); setNewPassword(''); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResetPassword(showResetPassword)}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h3 className="text-xl font-semibold text-gray-800">Edit User</h3>
              <p className="text-sm text-gray-500 mt-1">{showEditUser.email}</p>
            </div>
            
            <form onSubmit={handleEditUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={editUser.name}
                  onChange={(e) => setEditUser(prev => ({...prev, name: e.target.value}))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editUser.phone}
                  onChange={(e) => setEditUser(prev => ({...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10)}))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select
                  value={editUser.role_id}
                  onChange={(e) => setEditUser(prev => ({...prev, role_id: e.target.value, employee_id: '', employee_name: ''}))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Employee Selection - Shows when role requires it */}
              {needsEmployeeLinkage(editUser.role_id) && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <UserCheck className="w-5 h-5 text-blue-600" />
                    <label className="text-sm font-medium text-blue-800">
                      Link to Employee Record *
                    </label>
                  </div>
                  <p className="text-xs text-blue-600 mb-2">
                    This user will only see data assigned to the selected employee
                  </p>
                  <select
                    value={editUser.employee_id}
                    onChange={(e) => {
                      const emp = getFilteredEmployees(editUser.role_id).find(emp => emp.id === e.target.value);
                      setEditUser(prev => ({
                        ...prev, 
                        employee_id: e.target.value,
                        employee_name: emp?.name || ''
                      }));
                    }}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Employee</option>
                    {getFilteredEmployees(editUser.role_id).map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditUser(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg hover:bg-[#e89aa5]"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
